(() => {
  if (window.mumusoHeroInitialized) return;
  window.mumusoHeroInitialized = true;

  const heroSelector = '[data-mumuso-hero]';
  const slideSelector = '[data-hero-slide]';
  const transitionDuration = 1000;

  const getSlides = (hero) => Array.from(hero.querySelectorAll(slideSelector));
  const getTrack = (hero) => hero.querySelector('.mumuso-hero__track');
  const getSlideWidth = (hero) => {
    const slide = getSlides(hero)[0];
    return slide?.getBoundingClientRect().width || hero.getBoundingClientRect().width;
  };
  const normalizeIndex = (index, count) => (index + count) % count;

  const setSlideState = (hero, activeIndex) => {
    const slides = getSlides(hero);

    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.inert = !isActive;
    });
  };

  const setTrackPosition = (hero, position, animate = true) => {
    const track = getTrack(hero);
    if (!track) return;

    const slideWidth = getSlideWidth(hero);
    if (!slideWidth) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.style.transitionDuration = animate && !reduceMotion ? `${transitionDuration}ms` : '0ms';
    track.style.transform = `translate3d(${-position * slideWidth}px, 0, 0)`;
    hero.dataset.heroPosition = String(position);
  };

  const removeDuplicateIds = (element) => {
    if (element.id) element.removeAttribute('id');
    element.querySelectorAll('[id]').forEach((child) => child.removeAttribute('id'));
  };

  const prepareClone = (slide) => {
    const clone = slide.cloneNode(true);
    clone.removeAttribute('data-hero-slide');
    clone.removeAttribute('data-shopify-editor-block');
    clone.setAttribute('data-hero-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.classList.remove('is-active');
    clone.inert = true;
    removeDuplicateIds(clone);
    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
    return clone;
  };

  const syncTrackGeometry = (hero) => {
    const track = getTrack(hero);
    if (!track) return;

    const items = Array.from(track.children);
    const itemCount = items.length || 1;
    const itemBasis = `${100 / itemCount}%`;

    track.style.width = `${itemCount * 100}%`;
    items.forEach((slide) => {
      slide.style.flexBasis = itemBasis;
      slide.style.minWidth = itemBasis;
    });
  };

  const ensureLoop = (hero) => {
    const track = getTrack(hero);
    const slides = getSlides(hero);
    if (!track) return;
    if (slides.length < 2) {
      syncTrackGeometry(hero);
      return;
    }

    const clones = Array.from(track.querySelectorAll(':scope > [data-hero-clone]'));
    if (clones.length === 2 && track.firstElementChild === clones[0] && track.lastElementChild === clones[1]) {
      syncTrackGeometry(hero);
      return;
    }

    clones.forEach((clone) => clone.remove());

    track.prepend(prepareClone(slides.at(-1)));
    track.append(prepareClone(slides[0]));
    syncTrackGeometry(hero);
  };

  const completeTransition = (hero) => {
    if (!hero.mumusoHeroAnimating) return;

    window.clearTimeout(hero.mumusoHeroTransitionTimer);
    const slides = getSlides(hero);
    const position = Number.parseInt(hero.dataset.heroPosition || '1', 10);

    hero.mumusoHeroAnimating = false;
    if (position === 0) setTrackPosition(hero, slides.length, false);
    if (position === slides.length + 1) setTrackPosition(hero, 1, false);
  };

  const showSlide = (hero, nextIndex, direction = 'next', animate = true) => {
    const slides = getSlides(hero);
    if (!slides.length || hero.mumusoHeroAnimating) return false;

    const currentIndex = Number.parseInt(hero.dataset.heroIndex || '0', 10);
    const index = normalizeIndex(nextIndex, slides.length);
    let position = index + (slides.length > 1 ? 1 : 0);

    if (animate && slides.length > 1 && direction === 'next' && currentIndex === slides.length - 1 && index === 0) {
      position = slides.length + 1;
    } else if (animate && slides.length > 1 && direction === 'previous' && currentIndex === 0 && index === slides.length - 1) {
      position = 0;
    }

    hero.dataset.heroIndex = String(index);
    hero.dataset.heroDirection = direction;
    setSlideState(hero, index);
    ensureLoop(hero);
    setTrackPosition(hero, position, animate);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (animate && slides.length > 1 && !reduceMotion) {
      hero.mumusoHeroAnimating = true;
      hero.mumusoHeroTransitionTimer = window.setTimeout(() => completeTransition(hero), transitionDuration + 80);
    } else if (position === 0 || position === slides.length + 1) {
      hero.mumusoHeroAnimating = true;
      completeTransition(hero);
    }

    return true;
  };

  const pauseAutoplay = (hero) => {
    window.clearInterval(hero.mumusoHeroTimer);
    hero.mumusoHeroTimer = null;
  };

  const restartAutoplay = (hero) => {
    pauseAutoplay(hero);

    const slides = getSlides(hero);
    const autoplay = hero.dataset.autoplay === 'true';
    const autoplaySpeed = Number.parseInt(hero.dataset.autoplaySpeed || '4000', 10);

    if (
      !autoplay ||
      slides.length < 2 ||
      hero.mumusoHeroHovering ||
      hero.mumusoHeroFocusWithin ||
      hero.mumusoHeroInteractionPaused ||
      document.hidden
    ) return;

    hero.mumusoHeroTimer = window.setInterval(() => {
      const index = Number.parseInt(hero.dataset.heroIndex || '0', 10);
      showSlide(hero, index + 1, 'next');
    }, autoplaySpeed);
  };

  const resumeAutoplay = (hero) => restartAutoplay(hero);

  const moveBy = (hero, direction, interaction = false) => {
    const slides = getSlides(hero);
    if (slides.length < 2) return false;

    if (interaction) {
      hero.mumusoHeroInteractionPaused = true;
      pauseAutoplay(hero);
    }

    const index = Number.parseInt(hero.dataset.heroIndex || '0', 10);
    return showSlide(hero, index + direction, direction > 0 ? 'next' : 'previous');
  };

  const initHero = (hero) => {
    const slides = getSlides(hero);
    if (!slides.length) return;

    if (hero.mumusoHeroReady) {
      ensureLoop(hero);
      const currentIndex = normalizeIndex(Number.parseInt(hero.dataset.heroIndex || '0', 10), slides.length);
      setSlideState(hero, currentIndex);
      setTrackPosition(hero, currentIndex + (slides.length > 1 ? 1 : 0), false);
      return;
    }

    hero.mumusoHeroReady = true;

    let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (index < 0) index = Number.parseInt(hero.dataset.heroIndex || '0', 10);
    if (!Number.isFinite(index)) index = 0;

    ensureLoop(hero);
    hero.dataset.heroIndex = String(index);
    setSlideState(hero, index);
    setTrackPosition(hero, index + (slides.length > 1 ? 1 : 0), false);

    const track = getTrack(hero);
    track?.addEventListener('transitionend', (event) => {
      if (event.target === track && event.propertyName === 'transform') completeTransition(hero);
    });

    hero.addEventListener('mouseenter', () => {
      hero.mumusoHeroHovering = true;
      pauseAutoplay(hero);
    });
    hero.addEventListener('mouseleave', () => {
      hero.mumusoHeroHovering = false;
      resumeAutoplay(hero);
    });
    hero.addEventListener('focusin', () => {
      hero.mumusoHeroFocusWithin = true;
      pauseAutoplay(hero);
    });
    hero.addEventListener('focusout', () => {
      window.setTimeout(() => {
        hero.mumusoHeroFocusWithin = hero.contains(document.activeElement);
        resumeAutoplay(hero);
      }, 0);
    });

    restartAutoplay(hero);
  };

  const enableMobileSwipe = (hero) => {
    if (hero.mumusoSwipeReady) return;
    hero.mumusoSwipeReady = true;

    let startX = 0;
    let startY = 0;
    let activePointerId = null;
    let tracking = false;
    let blockedClick = false;
    let dragOrigin = 0;
    let dragged = false;

    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

    const beginSwipe = (clientX, clientY, pointerId = null) => {
      if (!isMobile() || tracking || hero.mumusoHeroAnimating || getSlides(hero).length < 2) return;

      tracking = true;
      dragged = false;
      activePointerId = pointerId;
      startX = clientX;
      startY = clientY;
      const index = Number.parseInt(hero.dataset.heroIndex || '0', 10);
      dragOrigin = -(index + 1) * getSlideWidth(hero);
      pauseAutoplay(hero);
      const track = getTrack(hero);
      if (track) track.style.transitionDuration = '0ms';
    };

    const trackSwipe = (event, clientX, clientY, pointerId = null) => {
      if (!tracking || !isMobile()) return;
      if (activePointerId !== null && pointerId !== activePointerId) return;

      const deltaX = clientX - startX;
      const deltaY = clientY - startY;

      if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY) * 1.1 && event.cancelable) {
        dragged = true;
        event.preventDefault();
        const track = getTrack(hero);
        if (track) track.style.transform = `translate3d(${dragOrigin + deltaX}px, 0, 0)`;
      }
    };

    const finishSwipe = (clientX, clientY, pointerId = null) => {
      if (!tracking || !isMobile()) return;
      if (activePointerId !== null && pointerId !== activePointerId) return;

      tracking = false;
      activePointerId = null;

      const deltaX = clientX - startX;
      const deltaY = clientY - startY;

      if (!dragged || Math.abs(deltaX) < 32 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) {
        const index = Number.parseInt(hero.dataset.heroIndex || '0', 10);
        showSlide(hero, index, deltaX < 0 ? 'next' : 'previous');
        restartAutoplay(hero);
        return;
      }

      hero.mumusoHeroInteractionPaused = true;
      blockedClick = true;
      window.setTimeout(() => {
        blockedClick = false;
      }, 350);

      moveBy(hero, deltaX < 0 ? 1 : -1, true);
    };

    const cancelSwipe = () => {
      if (!tracking) return;
      tracking = false;
      activePointerId = null;
      const index = Number.parseInt(hero.dataset.heroIndex || '0', 10);
      showSlide(hero, index, 'next');
      restartAutoplay(hero);
    };

    if (window.PointerEvent) {
      hero.addEventListener('pointerdown', (event) => {
        if (event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
        if (!isMobile()) return;
        beginSwipe(event.clientX, event.clientY, event.pointerId);
        if (hero.setPointerCapture && !event.target.closest('a[href]')) hero.setPointerCapture(event.pointerId);
      });

      hero.addEventListener('pointermove', (event) => {
        trackSwipe(event, event.clientX, event.clientY, event.pointerId);
      });

      hero.addEventListener('pointerup', (event) => {
        finishSwipe(event.clientX, event.clientY, event.pointerId);
        if (hero.releasePointerCapture && hero.hasPointerCapture?.(event.pointerId)) {
          hero.releasePointerCapture(event.pointerId);
        }
      });

      hero.addEventListener('pointercancel', cancelSwipe);
    } else {
      hero.addEventListener('touchstart', (event) => {
        if (!isMobile() || event.touches.length !== 1) return;
        beginSwipe(event.touches[0].clientX, event.touches[0].clientY);
      }, { passive: true });

      hero.addEventListener('touchmove', (event) => {
        if (!event.touches.length) return;
        trackSwipe(event, event.touches[0].clientX, event.touches[0].clientY);
      }, { passive: false });

      hero.addEventListener('touchend', (event) => {
        if (!event.changedTouches.length) return;
        finishSwipe(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
      }, { passive: true });

      hero.addEventListener('touchcancel', cancelSwipe, { passive: true });
    }

    hero.addEventListener(
      'click',
      (event) => {
        if (!blockedClick) return;

        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  };

  const initHeroes = (scope) => {
    scope.querySelectorAll(heroSelector).forEach((hero) => {
      initHero(hero);
      enableMobileSwipe(hero);
    });
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-hero-prev], [data-hero-next]');
    if (!button) return;

    const hero = button.closest(heroSelector);
    if (!hero) return;

    event.preventDefault();

    const index = Number.parseInt(hero.dataset.heroIndex || '0', 10);
    const direction = button.matches('[data-hero-prev]') ? -1 : 1;
    moveBy(hero, direction, true);
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll(heroSelector).forEach((hero) => {
      if (!hero.mumusoHeroReady || hero.mumusoHeroAnimating) return;
      const slides = getSlides(hero);
      const index = Number.parseInt(hero.dataset.heroIndex || '0', 10);
      setTrackPosition(hero, index + (slides.length > 1 ? 1 : 0), false);
    });
  });

  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll(heroSelector).forEach((hero) => {
      if (document.hidden) pauseAutoplay(hero);
      else resumeAutoplay(hero);
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    initHeroes(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    if (!(event.target instanceof Element)) return;

    const block = event.target.closest(slideSelector);
    if (!block) return;

    const hero = block.closest(heroSelector);
    if (!hero) return;

    hero.mumusoHeroInteractionPaused = true;
    showSlide(hero, getSlides(hero).indexOf(block), 'next', false);
    pauseAutoplay(hero);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const hero = event.target.matches?.(heroSelector) ? event.target : event.target.querySelector?.(heroSelector);
    if (!hero) return;
    pauseAutoplay(hero);
    window.clearTimeout(hero.mumusoHeroTransitionTimer);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHeroes(document));
  } else {
    initHeroes(document);
  }
})();
